// ==========================================
// NRI FIRE Calculator - Main App
// 5-Step Streamlined Wizard + Dashboard
// Route-based code splitting for optimal performance
// ==========================================

import { useState, useCallback, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { useAppStore, selectCurrentStep } from '@/store/useAppStore'
import { JourneyModal } from '@/components/journey'
import { analytics } from '@/lib/analytics'
import { setSentryContext } from '@/lib/analytics/sentry'
import type { WizardStep } from '@/types'

// ==========================================
// Lazy-loaded components for code splitting
// Each view loads only when needed
// ==========================================
const LandingPage = lazy(() => import('@/components/landing/LandingPage'))
const WizardContainer = lazy(() => import('@/components/wizard/WizardContainer'))
const DashboardContainer = lazy(() => import('@/components/dashboard/DashboardContainer'))
const CalculationScreen = lazy(() => import('@/components/wizard/CalculationScreen'))
// const AIChat = lazy(() => import('@/components/ai/AIChat')) // DISABLED - not adding value yet
const RNORCalculatorPage = lazy(() => import('@/components/tools/RNORCalculatorPage'))
const CityComparisonPage = lazy(() => import('@/components/tools/CityComparisonPage'))
const ExpertTaxFilingPage = lazy(() => import('@/components/tools/ExpertTaxFilingPage'))

// Guides pages - lazy loaded
const GuidesHub = lazy(() => import('@/components/guides/GuidesHub'))
const BeforeTheMoveGuide = lazy(() => import('@/components/guides/BeforeTheMoveGuide'))
const TransitionGuide = lazy(() => import('@/components/guides/TransitionGuide'))
const AfterLandingGuide = lazy(() => import('@/components/guides/AfterLandingGuide'))

// Wizard steps - lazy loaded for smaller initial bundle
const Step1Profile = lazy(() => import('@/components/wizard/Step1Profile'))
const Step2Location = lazy(() => import('@/components/wizard/Step2Location'))
const Step3Income = lazy(() => import('@/components/wizard/Step3Income'))
const Step4Assets = lazy(() => import('@/components/wizard/Step4Assets'))
const Step5SafetyNet = lazy(() => import('@/components/wizard/Step5SafetyNet'))
const Step5Plan = lazy(() => import('@/components/wizard/Step5Plan'))

// DevTools - only loaded in development, completely excluded from production bundle
const DevTools = import.meta.env.DEV
  ? lazy(() => import('@/components/dev/DevTools'))
  : () => null

// ==========================================
// Loading Spinner Component
// ==========================================
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-slate-600 text-sm font-medium">Loading...</p>
      </div>
    </div>
  )
}

// App view types
type AppView = 'landing' | 'wizard' | 'calculating' | 'dashboard'

// 6-Step Wizard Components Mapping
// Step 1: Your Profile (Profile + NRI Status)
// Step 2: Destination (City + Lifestyle + Housing)
// Step 3: Income (Earnings & Expenses)
// Step 4: Assets (Wealth & Liabilities)
// Step 5: Protection (Safety Net)
// Step 6: Plan (Goals + Assumptions)
const STEP_COMPONENTS = {
  1: Step1Profile,
  2: Step2Location,
  3: Step3Income,
  4: Step4Assets,
  5: Step5SafetyNet,
  6: Step5Plan,
} as const

// Main App Flow Component (Landing -> Wizard -> Dashboard)
function MainAppFlow() {
  const [view, setView] = useState<AppView>('landing')
  const [showJourneyModal, setShowJourneyModal] = useState(false)
  const currentStep = useAppStore(selectCurrentStep)
  const setCurrentStep = useAppStore((state) => state.setCurrentStep)
  const residentCountry = useAppStore((state) => state.residentCountryAssets?.countryCode)
  const navigate = useNavigate()
  const location = useLocation()

  // Track page views on route changes
  useEffect(() => {
    analytics.pageView(location.pathname)
  }, [location.pathname])

  // Update Sentry context when view/step changes
  useEffect(() => {
    setSentryContext({
      country: residentCountry as 'USA' | 'UAE' | undefined,
      wizardStep: view === 'wizard' ? currentStep : undefined,
      dashboardTab: view === 'dashboard' ? 'summary' : undefined,
    })
  }, [view, currentStep, residentCountry])

  // Check if coming from /calculator or /dashboard route (direct links)
  useEffect(() => {
    if (location.pathname === '/calculator') {
      setView('wizard')
    } else if (location.pathname === '/dashboard') {
      setView('dashboard')
    }
  }, [location.pathname])

  // Listen for navigate-to-wizard events from dashboard components (e.g., TaxTab)
  useEffect(() => {
    const handleNavigateToWizard = (event: CustomEvent<{ step?: number }>) => {
      const targetStep = event.detail?.step as WizardStep | undefined
      if (targetStep && targetStep >= 1 && targetStep <= 6) {
        setCurrentStep(targetStep)
      }
      setView('wizard')
      navigate('/calculator', { replace: true })
    }

    window.addEventListener('navigate-to-wizard', handleNavigateToWizard as EventListener)
    return () => {
      window.removeEventListener('navigate-to-wizard', handleNavigateToWizard as EventListener)
    }
  }, [setCurrentStep, navigate])

  // Listen for open-journey-modal events from dashboard components
  useEffect(() => {
    const handleOpenJourneyModal = () => {
      setShowJourneyModal(true)
    }

    window.addEventListener('open-journey-modal', handleOpenJourneyModal)
    return () => {
      window.removeEventListener('open-journey-modal', handleOpenJourneyModal)
    }
  }, [])

  // Handle start planning
  const handleStartPlanning = useCallback(() => {
    analytics.wizardStart('landing_hero')
    setView('wizard')
    navigate('/calculator', { replace: true })
  }, [navigate])

  // Handle wizard completion - show calculation screen
  const handleWizardComplete = useCallback(() => {
    setView('calculating')
  }, [])

  // Handle calculation complete (skip journey)
  const handleCalculationComplete = useCallback(() => {
    setView('dashboard')
    navigate('/dashboard', { replace: true })
  }, [navigate])

  // Handle "See Your Roadmap" button - show journey modal
  const handleViewJourney = useCallback(() => {
    setShowJourneyModal(true)
  }, [])

  // Handle journey modal close - go to dashboard
  const handleJourneyModalClose = useCallback(() => {
    setShowJourneyModal(false)
    setView('dashboard')
    navigate('/dashboard', { replace: true })
  }, [navigate])

  // Handle edit inputs from dashboard
  const handleEditInputs = useCallback(() => {
    setView('wizard')
  }, [])

  // Render current step component
  const renderStepContent = () => {
    const StepComponent = STEP_COMPONENTS[currentStep as keyof typeof STEP_COMPONENTS]
    if (!StepComponent) return null
    return (
      <Suspense fallback={<div className="p-8 text-center text-slate-500">Loading step...</div>}>
        <StepComponent />
      </Suspense>
    )
  }

  // Get the current view key for AnimatePresence
  const getCurrentViewKey = () => {
    return view
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {/* Main view transitions - only one view at a time */}
      <AnimatePresence mode="wait">
        <motion.div
          key={getCurrentViewKey()}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {view === 'landing' && (
            <LandingPage onStart={handleStartPlanning} />
          )}

          {view === 'wizard' && (
            <Suspense fallback={<LoadingSpinner />}>
              <WizardContainer onComplete={handleWizardComplete}>
                {renderStepContent()}
              </WizardContainer>
            </Suspense>
          )}

          {view === 'calculating' && (
            <CalculationScreen
              onComplete={handleCalculationComplete}
              onViewJourney={handleViewJourney}
            />
          )}

          {view === 'dashboard' && (
            <DashboardContainer onEditInputs={handleEditInputs} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Overlay components - outside AnimatePresence to avoid animation conflicts */}

      {/* AI Chat - DISABLED for now (doesn't add much value yet)
      {(view === 'wizard' || view === 'dashboard') && (
        <Suspense fallback={null}>
          <AIChat />
        </Suspense>
      )}
      */}

      {/* Journey Modal - shown after calculation when user clicks "See Your Roadmap" */}
      <JourneyModal
        isOpen={showJourneyModal}
        onClose={handleJourneyModalClose}
        autoGenerate={true}
      />
    </Suspense>
  )
}

// ==========================================
// Main App with Routing
// ==========================================
function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Standalone RNOR Calculator - SEO landing page */}
        <Route path="/rnor-calculator" element={<RNORCalculatorPage />} />

        {/* Standalone City Comparison - SEO landing page */}
        <Route path="/city-comparison" element={<CityComparisonPage />} />

        {/* Expert Tax Filing - Our CA Service */}
        <Route path="/expert-tax-filing" element={<ExpertTaxFilingPage />} />

        {/* Guides - SEO content hub */}
        <Route path="/guides" element={<GuidesHub />} />
        <Route path="/guides/before-the-move" element={<BeforeTheMoveGuide />} />
        <Route path="/guides/transition" element={<TransitionGuide />} />
        <Route path="/guides/after-landing" element={<AfterLandingGuide />} />

        {/* Main app flow - handles /, /calculator, /dashboard */}
        <Route path="/*" element={<MainAppFlow />} />
      </Routes>

      {/* Dev Tools - completely excluded from production bundle */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <DevTools />
        </Suspense>
      )}

      {/* Vercel Speed Insights */}
      <SpeedInsights />
    </Suspense>
  )
}

export default App
